import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "rizzui";
import { Field } from "@steelyard/theme";

/** Ported from apps/admin/components/customer-trade-terms-queue.tsx. */
export type TradeTermsRequest = { requestId: string; customerId: string; customerNumber?: string; displayName?: string; requestedAt?: string; status?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** `GET /admin/trade-terms` declares no response body, so this is parsed rather than cast. */
export function parseTradeTermsQueue(body: unknown): TradeTermsRequest[] {
  const raw = Array.isArray(body) ? body : isRecord(body) ? (body.items ?? body.requests ?? body.tradeTerms) : undefined;

  if (!Array.isArray(raw)) {
    throw new Error(
      "GET /admin/trade-terms answered without a request array. Rendering the queue empty would report that " +
        "nobody is waiting for a decision.",
    );
  }

  return raw.flatMap((entry) => {
    if (!isRecord(entry)) return [];
    // Older bodies call it `id`; either way it is a key, never shown.
    const requestId = typeof entry.requestId === "string" ? entry.requestId : entry.id;
    const customerId = entry.customerId;
    if (typeof requestId !== "string" || typeof customerId !== "string") return [];
    return [
      {
        requestId,
        customerId,
        customerNumber: typeof entry.customerNumber === "string" ? entry.customerNumber : undefined,
        // `customerName` is Customers' own label for the request's customer.
        displayName: typeof entry.customerName === "string" ? entry.customerName : typeof entry.displayName === "string" ? entry.displayName : undefined,
        requestedAt: typeof entry.requestedAt === "string" ? entry.requestedAt : undefined,
        status: typeof entry.status === "string" ? entry.status : undefined,
      },
    ];
  });
}

const declineSchema = z.object({ reason: z.string().trim().min(1, "Required").max(500) });

/** A decline is notified to the customer — not a suspension, they stay on the Default Tier. */
export function DeclineTradeTermsForm({
  onSubmit,
  customerName,
  requestId,
}: {
  onSubmit: (formData: FormData) => void | Promise<void>;
  customerName: string;
  requestId: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof declineSchema>>({ resolver: zodResolver(declineSchema), defaultValues: { reason: "" } });

  const submit = handleSubmit(async (values) => {
    const formData = new FormData();
    formData.set("reason", values.reason);
    await onSubmit(formData);
  });

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3">
      <Field
        label="Why is this being declined?"
        required
        id={`decline-reason-${requestId}`}
        maxLength={500}
        hint={`Sent to ${customerName}. They stay on the Default Tier and keep ordering at piece prices — a decline is not a suspension.`}
        error={errors.reason?.message}
        {...register("reason")}
      />
      <Button type="submit" variant="outline" className="self-start" disabled={isSubmitting}>
        Decline the request
      </Button>
    </form>
  );
}
